import { useState, type CSSProperties, type SyntheticEvent } from "react";

export interface LauncherPreviewProps {
    displayName: string;
    primaryColor: string;
    iconUrl?: string | null;
    welcomeMessage?: string | null;
}

const LAUNCHER_HEIGHT_PX = 56;
const LAUNCHER_MAX_WIDTH_PX = 206;
const LOGO_HEIGHT_PX = LAUNCHER_HEIGHT_PX / Math.SQRT2;

/**
 * Visual stub of the launcher button + welcome bubble. Mirrors the chrome the
 * real widget renders so admins can sanity-check color/icon/copy without
 * leaving the wizard.
 *
 * Launcher is an ellipse whose width follows the logo's natural aspect: the
 * logo's four corners land on the ellipse boundary. Same geometry as the
 * widget's `sizeLauncherOvalToLogo` and the iframe's `LauncherOvalIcon`.
 */
export default function LauncherPreview({
    displayName,
    primaryColor,
    iconUrl,
    welcomeMessage
}: LauncherPreviewProps) {
    const [aspect, setAspect] = useState<number | null>(null);
    const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            setAspect(img.naturalWidth / img.naturalHeight);
        }
    };

    // Cap the oval width at LAUNCHER_MAX_WIDTH_PX. When the cap kicks in
    // (extremely wide logos), the logo is `contain`-fit rather than
    // corner-touch, so we don't blow up the preview tile.
    const idealWidth = aspect ? LAUNCHER_HEIGHT_PX * aspect : LAUNCHER_HEIGHT_PX;
    const ovalWidth = Math.min(idealWidth, LAUNCHER_MAX_WIDTH_PX);
    const capped = idealWidth > LAUNCHER_MAX_WIDTH_PX;

    const buttonStyle: CSSProperties = {
        background: primaryColor || "#1976d2",
        width: ovalWidth,
        height: LAUNCHER_HEIGHT_PX,
        borderRadius: "50%"
    };
    const imgStyle: CSSProperties = capped
        ? {
              // Cap hit: render logo within the capped oval, contain-fit.
              maxHeight: LOGO_HEIGHT_PX,
              maxWidth: LAUNCHER_MAX_WIDTH_PX - 16,
              objectFit: "contain"
          }
        : {
              height: LOGO_HEIGHT_PX,
              width: aspect ? LOGO_HEIGHT_PX * aspect : "auto",
              objectFit: "contain"
          };

    return (
        <div className="integration-launcher-preview">
            <div className="integration-launcher-preview__bubble">
                <strong>{displayName || "Assistant"}</strong>
                <p>{welcomeMessage || "Hi! How can I help?"}</p>
            </div>
            <button
                type="button"
                className="integration-launcher-preview__button"
                style={buttonStyle}
                aria-label="Launcher preview"
            >
                {iconUrl ? (
                    <img src={iconUrl} alt="" style={imgStyle} onLoad={handleLoad} />
                ) : (
                    <span className="integration-launcher-preview__initial">
                        {(displayName || "A").slice(0, 1).toUpperCase()}
                    </span>
                )}
            </button>
        </div>
    );
}
